import React from 'react';
import { Box, Typography, Avatar, Button } from '@mui/material';
import ActivityLogSkeleton from '../Custom/Skeletons/ActivityLogSkeleton'; // Skeleton component for loading state
import { format } from 'date-fns';


import '../../css/main.css';
import ScrollAwareTooltip from './ScrollAwareTooltip';


const parseMentions = (text, asJsx = false) => {
    if (!text) return text;
    if (!asJsx) {
        return text.replace(/@\[([^\]]+)\]\([^)]+\)/g, (_, name) => `@${name}`);
    }

    const parts = [];
    let lastIndex = 0;
    const regex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        const [, name, userId] = match;
        parts.push(
            <a key={match.index} href={`/workflow/profile/${userId}`}>@{name}</a>
        );
        lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts;
};

const ActivityLog = ({ taskActivityLog, myUserTimezone, showAllActivity, toggleShowAllActivity, isLoadingActivityLog, fetchAllActivityLogs, hasMoreActivityLogs, t }) => {
    const getParsedMessage = (log) => {

        if (log.action === "comment_edited") {
            const oldText = parseMentions(log.metadata?.old_text || "", true);
            const newText = parseMentions(log.metadata?.new_text || "", true);

            return (
                <div className="view-task-row-flex" style={{ flexDirection: "column" }}>
                    <Typography>
                        {log.user.full_name} edited a comment
                    </Typography>
                    <ScrollAwareTooltip
                        title={
                            <>
                                <span>{t('global.old')}: {oldText}</span><br />
                                <span>{t('global.new')}: {newText}</span>
                            </>
                        }
                        placement="top"
                        arrow
                    >
                        <Typography className="view-task-tooltip" sx={{ fontStyle: "italic", cursor: "pointer" }}>
                            "{oldText}" → "{newText}"
                        </Typography>
                    </ScrollAwareTooltip>
                </div>
            );
        }

        const changes = log.changes ? log.changes.split(", ") : [];
        if (log.log_message.includes("added member") || log.log_message.includes("removed member")) {
            const searchTerm = log.log_message.includes("added member") ? "added member" : "removed member";
            const index = log.log_message.indexOf(searchTerm) + searchTerm.length;
            const beforeText = log.log_message.substring(0, index) + " ";
            const afterText = log.log_message.substring(index).trim();
            const nameEndIndex = afterText.indexOf(",") === -1 ? afterText.length : afterText.indexOf(",");
            const name = afterText.substring(0, nameEndIndex);
            const restOfMessage = afterText.substring(nameEndIndex);
            return (
                <span>
                    {beforeText}
                    <a href={`/workflow/profile/${log.referencing}`}>{name}</a>
                    {restOfMessage}
                </span>
            );
        } else if (changes.length > 0 && log.type !== "label") {
            const [field, values] = changes[0].split(" changed from ") || ["", ""];
            const [oldVal, newVal] = values ? values.split(" to ") : ["", ""];
            return (
                <div className="view-task-row-flex">
                    {log.log_message.replace(field.trim(), "").trim()}
                    <ScrollAwareTooltip title={<><span>{t('global.old')}: {oldVal?.trim()}</span><br /><span>{t('global.new')}: {newVal?.trim()}</span></>} placement="top" arrow>
                        <Typography className="view-task-tooltip">&nbsp;{field.trim()}.</Typography>
                    </ScrollAwareTooltip>
                </div>
            );
        } else {
            return parseMentions(log.log_message, true);
        }
    };

    return (
        <>
            <Box className="opacity-box">
                <Typography className="view-task-activity-log-margin">{t('project_details.activity_log')}</Typography>
                <ul className="view-task-activity-log-div">
                    {isLoadingActivityLog ? (
                        <>
                            <ActivityLogSkeleton />
                            <ActivityLogSkeleton />
                            <ActivityLogSkeleton />
                            <ActivityLogSkeleton />
                            <ActivityLogSkeleton />
                        </>
                    ) : taskActivityLog?.length > 0 ? (
                        taskActivityLog.map((log, index) => (
                            <li key={index} className="view-task-activity-log-list">
                                <Avatar src={`${log.user_image}`} alt={log.user?.username} className="view-task-activity-log-avatar-margin" />
                                <div>
                                    <Typography>{getParsedMessage(log)}</Typography>
                                    <Typography className="view-task-activity-log-date-text" variant="subtitle2">
                                        {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm')}
                                    </Typography>
                                </div>
                            </li>
                        ))
                    ) : (
                        <li key="no-activity" className="white-txt-color view-task-activity-log-margin">
                            <Typography variant="body2" color="textSecondary">
                                <span className="white-txt-color">{t('project_details.no_activity')}</span>
                            </Typography>
                        </li>
                    )}
                </ul>
                {hasMoreActivityLogs && !showAllActivity && (
                    <Button
                        onClick={() => {
                            toggleShowAllActivity();
                            if (!showAllActivity) {
                                fetchAllActivityLogs(); // Fetch all activity logs if only partial are shown
                            }
                        }}
                        className="button-dark-bg view-task-show-more-activity-log"
                    >
                        {t("modal.show_all_activity")}

                    </Button>
                )}
            </Box>
        </>
    );
};

export default ActivityLog;